import produce from "immer";
import { keyBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { ApiResponse, fetchApi } from "../../utils/api";
import { DataById } from "../../utils/types";
import { LoadingKey, useLoading } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  notificationSubscriptions: DataById<NotificationSubscription>;
  fetchNotificationSubscriptions: () => Promise<
    DataById<NotificationSubscription> | undefined
  >;
  toggleNotificationSubscription: (
    notificationSubscription: NotificationSubscription
  ) => Promise<ApiResponse<NotificationSubscription[]>>;
};

export const NotificationSubscriptionsContext = React.createContext<
  ContextValue | undefined
>(undefined);

export const NotificationSubscriptionsProvider: React.FC<Props> = (props) => {
  const { currentWorkspaceId } = useWorkspaces();
  const [notificationSubscriptions, setNotificationSubscriptions] = useState<
    DataById<NotificationSubscription>
  >({});
  const { addLoader, removeLoader } = useLoading();

  const fetchNotificationSubscriptions = useCallback(async () => {
    if (!currentWorkspaceId) return;

    addLoader(LoadingKey.workspaceNotificationSubscriptions);

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/notifications`
    );

    const apiResponse: ApiResponse<NotificationSubscription[]> =
      await response.json();

    let newNotificationSubscriptions: DataById<NotificationSubscription> = {};

    if (apiResponse.status) {
      newNotificationSubscriptions = keyBy(apiResponse.data, "descriptive_key");

      setNotificationSubscriptions(newNotificationSubscriptions);
    }

    removeLoader(LoadingKey.workspaceNotificationSubscriptions);

    return newNotificationSubscriptions;
  }, [addLoader, currentWorkspaceId, removeLoader]);

  useEffect(() => {
    fetchNotificationSubscriptions();
  }, [fetchNotificationSubscriptions]);

  const toggleNotificationSubscription = useCallback(
    async (notificationSubscription: NotificationSubscription) => {
      setNotificationSubscriptions((prevState) =>
        produce(prevState, (draft) => {
          draft[notificationSubscription.descriptive_key].subscribed =
            !draft[notificationSubscription.descriptive_key].subscribed;
        })
      );

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/notification/${notificationSubscription.uuid}`,
        {
          method: notificationSubscription.subscribed ? "DELETE" : "POST"
        }
      );

      const apiResponse: ApiResponse<NotificationSubscription[]> =
        await response.json();

      if (apiResponse.status) {
        await fetchNotificationSubscriptions();
      }

      return apiResponse;
    },
    [currentWorkspaceId, fetchNotificationSubscriptions]
  );

  const value = useMemo(
    () => ({
      notificationSubscriptions,
      fetchNotificationSubscriptions,
      toggleNotificationSubscription
    }),
    [
      fetchNotificationSubscriptions,
      notificationSubscriptions,
      toggleNotificationSubscription
    ]
  );

  return <NotificationSubscriptionsContext.Provider value={value} {...props} />;
};

export const useNotificationSubscriptions = (): ContextValue => {
  const context = useContext(NotificationSubscriptionsContext);

  if (context === undefined) {
    throw new Error(
      "useNotificationSubscriptions must be used within an NotificationSubscriptionsProvider"
    );
  }

  return context;
};

//
// Utils
//

export enum NotificationSubscriptionKey {
  hotspotStatus = "online-status-change"
}

interface NotificationSubscription {
  uuid: string;
  descriptive_key: NotificationSubscriptionKey;
  subscribed: boolean;
}

export const notificationSubscriptionOptions: {
  [key in NotificationSubscriptionKey]: {
    title: string;
    subtitle: string;
  };
} = {
  [NotificationSubscriptionKey.hotspotStatus]: {
    title: "Hotspot active/inactive status",
    subtitle:
      "Receive an email at most once a day if any of your hotspots had changes in their active/inactive status."
  }
};
